import { useState } from 'react';
import { useIndustryTerms } from '@/hooks/useIndustryTerms';
import {
  Plus,
  MoreHorizontal,
  Loader2,
  ChefHat,
  Trash2,
  Edit2,
  Check,
  Tag,
  MonitorPlay,
  MapPin,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Checkbox } from '@/components/ui/checkbox';
import { useKitchenStations, type KitchenStation, type CreateKitchenStationInput } from '@/hooks/useKitchenStations';
import { useProducts } from '@/hooks/useProducts';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';
import { LocationSelector } from '@/components/location/LocationSelector';
import { useLocations } from '@/hooks/useLocations';

// Predefined colors for stations
const STATION_COLORS = [
  { value: '#6366f1', label: 'Indigo' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#ef4444', label: 'Red' },
  { value: '#f97316', label: 'Orange' },
  { value: '#eab308', label: 'Yellow' },
  { value: '#22c55e', label: 'Green' },
  { value: '#14b8a6', label: 'Teal' },
  { value: '#0ea5e9', label: 'Sky' },
  { value: '#6b7280', label: 'Gray' },
];

interface StationFormData {
  name: string;
  description: string;
  color: string;
  display_categories: string[];
  is_active: boolean;
  location_id: string;
}

const defaultFormData: StationFormData = {
  name: '',
  description: '',
  color: '#6366f1',
  display_categories: [],
  is_active: true,
  location_id: '',
};

export default function KitchenStations() {
  const { currentLocation, setCurrentLocation } = useLocationStore();
  const { isSuperadmin, isAdmin } = useAuth();
  const terms = useIndustryTerms();
  const { locations } = useLocations();

  // Track whether "All Locations" is selected
  const [showAllLocations, setShowAllLocations] = useState(false);

  const {
    stations,
    isLoading,
    createStation,
    updateStation,
    deleteStation,
    toggleStationActive,
  } = useKitchenStations({ allLocations: showAllLocations });

  const { categories } = useProducts();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingStation, setEditingStation] = useState<KitchenStation | null>(null);
  const [formData, setFormData] = useState<StationFormData>(defaultFormData);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canManageStations = isSuperadmin || isAdmin;
  const hasMultipleLocations = locations.length > 1;

  // Handle location change from selector
  const handleLocationSelect = (locationId: string) => {
    const location = locations.find(l => l.id === locationId);
    if (location) {
      setCurrentLocation(location);
      setShowAllLocations(false);
    }
  };

  // Handle "All Locations" selection
  const handleAllLocationsSelect = () => {
    setShowAllLocations(true);
  };

  // Get location name by ID (for showing in station cards)
  const getLocationName = (locationId: string): string => {
    return locations.find(l => l.id === locationId)?.name || 'Unknown Location';
  };

  const handleOpenCreate = () => {
    // Default to current location, or first available location if viewing all
    const defaultLocationId = currentLocation?.id || locations[0]?.id || '';
    setFormData({ ...defaultFormData, location_id: defaultLocationId });
    setFormError(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (station: KitchenStation) => {
    setFormData({
      name: station.name,
      description: station.description || '',
      color: station.color || '#6366f1',
      display_categories: station.display_categories || [],
      is_active: station.is_active,
      location_id: station.location_id,
    });
    setFormError(null);
    setEditingStation(station);
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditingStation(null);
    setFormData(defaultFormData);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name.trim()) {
      setFormError('Station name is required');
      return;
    }

    if (!formData.location_id) {
      setFormError('Please select a location');
      return;
    }

    try {
      if (editingStation) {
        await updateStation.mutateAsync({
          id: editingStation.id,
          updates: {
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            color: formData.color,
            display_categories: formData.display_categories,
            is_active: formData.is_active,
          },
        });
        toast.success('Station updated', {
        description: `"${formData.name}" has been updated`,
      });
      } else {
        const input: CreateKitchenStationInput = {
          location_id: formData.location_id,
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          color: formData.color,
          display_categories: formData.display_categories,
          is_active: formData.is_active,
        };
        await createStation.mutateAsync(input);
        const locationName = getLocationName(formData.location_id);
        toast.success('Station created', {
        description: `"${formData.name}" has been added to ${locationName}`,
      });
      }
      handleCloseDialog();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save station';
      setFormError(message);
    }
  };

  const handleDelete = async (station: KitchenStation) => {
    if (!confirm(`Are you sure you want to delete "${station.name}"? Products assigned to this station will be unassigned.`)) {
      return;
    }

    setDeletingId(station.id);
    try {
      await deleteStation.mutateAsync(station.id);
      toast.success('Station deleted', {
        description: `"${station.name}" has been removed`,
      });
    } catch {
      toast.error('Error', {
        description: 'Failed to delete station',
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (station: KitchenStation, isActive: boolean) => {
    try {
      await toggleStationActive.mutateAsync({ id: station.id, is_active: isActive });
      toast.success(isActive ? 'Station enabled' : 'Station disabled', {
        description: `"${station.name}" is now ${isActive ? 'active' : 'inactive'}`,
      });
    } catch {
      toast.error('Error', {
        description: 'Failed to update station status',
      });
    }
  };

  const handleCategoryToggle = (categoryId: string) => {
    setFormData(prev => {
      const current = prev.display_categories;
      if (current.includes(categoryId)) {
        return { ...prev, display_categories: current.filter(id => id !== categoryId) };
      } else {
        return { ...prev, display_categories: [...current, categoryId] };
      }
    });
  };

  const getCategoryNames = (categoryIds: string[] | null): string => {
    if (!categoryIds || categoryIds.length === 0) return 'All categories';
    const names = categoryIds
      .map(id => categories.find(c => c.id === id)?.name)
      .filter(Boolean);
    if (names.length === 0) return 'All categories';
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  };

  // Header with location selector
  const header = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-indigo-500/10">
          <MonitorPlay className="h-6 w-6 text-indigo-500" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            {terms.kitchenStations}
          </h1>
          <p className="text-sm text-muted-foreground">
            {stations.length} station{stations.length !== 1 ? 's' : ''} configured
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Location selector */}
        <LocationSelector
          showAllOption={hasMultipleLocations}
          value={showAllLocations ? 'all' : currentLocation?.id}
          controlled={true}
          onLocationSelect={handleLocationSelect}
          onAllLocationsSelect={handleAllLocationsSelect}
        />

        {/* Add Station button */}
        {canManageStations && (showAllLocations || currentLocation) && (
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Station
          </Button>
        )}
      </div>
    </div>
  );

  // If no location selected and not showing all, prompt to select
  if (!currentLocation && !showAllLocations) {
    return (
      <DashboardLayout header={header}>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Please select a location to view kitchen stations</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout header={header}>
      <div className="space-y-6">

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <ChefHat className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  How Multi-KDS Routing Works
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  When an order is placed, items are automatically routed to the appropriate kitchen station:
                </p>
                <ul className="text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1 mt-2">
                  <li>Products with a direct station assignment go to that station first</li>
                  <li>Otherwise, the product's category determines the station</li>
                  <li>If no match, items go to an "All" station or the first active station</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stations List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : stations.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ChefHat className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium mb-2">No {terms.kitchenStations}</h3>
              <p className="text-muted-foreground text-center max-w-md mb-4">
                {showAllLocations
                  ? 'No kitchen stations have been created for any location yet.'
                  : 'Create kitchen stations to route orders to different prep areas like Main Kitchen, Bar, or Dessert Station.'}
              </p>
              {canManageStations && (showAllLocations || currentLocation) && (
                <Button onClick={handleOpenCreate}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create First Station
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {stations.map((station) => (
              <Card
                key={station.id}
                className={cn(
                  'relative transition-all',
                  !station.is_active && 'opacity-60'
                )}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-1 rounded-t-lg"
                  style={{ backgroundColor: station.color || '#6366f1' }}
                />
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${station.color || '#6366f1'}20` }}
                      >
                        <ChefHat
                          className="h-5 w-5"
                          style={{ color: station.color || '#6366f1' }}
                        />
                      </div>
                      <div>
                        <CardTitle className="text-base">{station.name}</CardTitle>
                        {station.description && (
                          <CardDescription className="text-sm mt-0.5">
                            {station.description}
                          </CardDescription>
                        )}
                        {/* Show location when viewing all locations */}
                        {showAllLocations && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            <span>{station.location?.name || getLocationName(station.location_id)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {canManageStations && !showAllLocations && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenEdit(station)}>
                            <Edit2 className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDelete(station)}
                            className="text-destructive focus:text-destructive"
                            disabled={deletingId === station.id}
                          >
                            {deletingId === station.id ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="mr-2 h-4 w-4" />
                            )}
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Categories */}
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {getCategoryNames(station.display_categories)}
                    </span>
                  </div>

                  {/* Active toggle */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant={station.is_active ? 'default' : 'secondary'}>
                        {station.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {canManageStations && !showAllLocations && (
                      <Switch
                        checked={station.is_active}
                        onCheckedChange={(checked) => handleToggleActive(station, checked)}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <ResponsiveModal
        open={createDialogOpen || !!editingStation}
        onOpenChange={(open) => !open && handleCloseDialog()}
      >
        <ResponsiveModalContent>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingStation ? 'Edit Station' : 'Create Kitchen Station'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {editingStation
                ? 'Update the station configuration'
                : 'Add a new kitchen display station for order routing'}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <form onSubmit={handleSubmit}>
            <ResponsiveModalBody className="space-y-4">
              {formError && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {formError}
                </div>
              )}

              {/* Location (only shown when creating, not editing) */}
              {!editingStation && (
                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Select
                    value={formData.location_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, location_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a location">
                        {formData.location_id && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{getLocationName(formData.location_id)}</span>
                          </div>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{loc.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Station Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Main Kitchen, Bar, Juice Bar"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description for this station"
                  rows={2}
                />
              </div>

              {/* Color */}
              <div className="space-y-2">
                <Label>Station Color</Label>
                <div className="flex flex-wrap gap-2">
                  {STATION_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                      className={cn(
                        'h-8 w-8 rounded-full border-2 transition-all flex items-center justify-center',
                        formData.color === color.value
                          ? 'border-foreground scale-110'
                          : 'border-transparent hover:scale-105'
                      )}
                      style={{ backgroundColor: color.value }}
                      title={color.label}
                    >
                      {formData.color === color.value && (
                        <Check className="h-4 w-4 text-white" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categories */}
              <div className="space-y-2">
                <Label>Route Categories to This Station</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Select which product categories should be routed to this station. Leave empty to accept all categories.
                </p>
                <div className="border rounded-md max-h-48 overflow-y-auto">
                  {categories.length === 0 ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      No categories found. Create categories in Menu first.
                    </div>
                  ) : (
                    <div className="divide-y">
                      {categories.map((category) => (
                        <label
                          key={category.id}
                          className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer"
                        >
                          <Checkbox
                            checked={formData.display_categories.includes(category.id)}
                            onCheckedChange={() => handleCategoryToggle(category.id)}
                          />
                          <span className="text-sm">{category.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label htmlFor="is_active" className="cursor-pointer">Active</Label>
                  <p className="text-xs text-muted-foreground">
                    Inactive stations won't receive orders
                  </p>
                </div>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
              </div>
            </ResponsiveModalBody>

            <ResponsiveModalFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createStation.isPending || updateStation.isPending}
              >
                {(createStation.isPending || updateStation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {editingStation ? 'Save Changes' : 'Create Station'}
              </Button>
            </ResponsiveModalFooter>
          </form>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </DashboardLayout>
  );
}
